import { AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { useEffect } from "react";
import { useNitroStore } from "../../../store/Store";
import { useIsMobile } from "../../../hooks/useIsMobile";

const animateZoomTo = (
  map: google.maps.Map,
  targetZoom: number,
  stepDelayMs: number = 90,
) => {
  const startZoom = map.getZoom() ?? targetZoom;
  const boundedTargetZoom = Math.max(0, Math.min(20, Math.round(targetZoom)));

  if (startZoom === boundedTargetZoom) {
    return;
  }

  const direction = startZoom < boundedTargetZoom ? 1 : -1;

  const tick = (currentZoom: number) => {
    const nextZoom = currentZoom + direction;
    map.setZoom(nextZoom);

    if (nextZoom !== boundedTargetZoom) {
      window.setTimeout(() => tick(nextZoom), stepDelayMs);
    }
  };

  window.setTimeout(() => tick(startZoom), stepDelayMs);
};

const normalizeCoordinates = (
  rawLat: number,
  rawLng: number,
  country?: string,
) => {
  let lat = Number(rawLat);
  let lng = Number(rawLng);
  let adjusted = false;

  // Some upstream sources occasionally send coordinates as [lng, lat].
  if (Math.abs(lat) > 90 && Math.abs(lat) <= 180 && Math.abs(lng) <= 90) {
    [lat, lng] = [lng, lat];
    adjusted = true;
  }

  const countryCode = (country || "").trim().toLowerCase();
  const isUsLocation =
    countryCode === "us" ||
    countryCode === "usa" ||
    countryCode === "united states" ||
    countryCode === "united states of america";

  // US longitudes should be west/negative. Correct obvious sign issues.
  if (isUsLocation && lng > 0 && lng <= 180) {
    lng = -lng;
    adjusted = true;
  }

  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lng) ||
    (lat === 0 && lng === 0) ||
    lat > 90 ||
    lat < -90 ||
    lng > 180 ||
    lng < -180
  ) {
    return undefined;
  }

  return { lat, lng, adjusted };
};

const SelectedLocation = () => {
  const map = useMap();
  const isMobile = useIsMobile(1334);

  const [
    pinLocation,
    tempPinLocation,
    selectedLocation,
    clearPanToBounds,
    selectingPinLocation,
    showAddLocationPanel,
    showInsightsPanel,
    insightsEditing,
  ] = useNitroStore((state) => [
    state.pinLocation,
    state.tempPinLocation,
    state.selectedLocation,
    state.clearPanToBounds,
    state.selectingPinLocation,
    state.showAddLocationPanel,
    state.showInsightsPanel,
    state.insightsEditing,
  ]);

  useEffect(() => {
    if (!map) return;
    if (tempPinLocation) {
      if (tempPinLocation.lat == null || tempPinLocation.lng == null) {
        // Don't panTo when location not provided (null/undefined). Allow 0 coordinates.
        return;
      }

      const normalized = normalizeCoordinates(
        tempPinLocation.lat,
        tempPinLocation.lng,
        selectedLocation?.country,
      );
      if (!normalized) {
        return;
      }
      const { lat, lng } = normalized;

      const currentZoom = map.getZoom() || 0;
      // Keep Location Overview behavior deterministic across first and subsequent clicks.
      // Some multi-location contexts previously used incremental +3 zoom (8 -> 11 -> 14),
      // which made the first click appear less zoomed than later clicks.
      const targetZoom = currentZoom < 14 ? 14 : currentZoom;

      // Center pin in the visible area of the map (exclude area behind addLocation panel / Insights panel).
      // Use Mercator degrees-per-pixel at the TARGET zoom to avoid stale getBounds() values.
      // In Mercator: lngDegsPerPx = 360 / (256 * 2^zoom). Panel is 620px, so offset = 310px * degsPerPx.
      const lngDegsPerPx = 360 / (256 * 2 ** targetZoom);
      const mapWidth = window.innerWidth - 400;
      const panelOffsetPx = mapWidth > 0 ? 310 : 0;
      const offset = lngDegsPerPx * panelOffsetPx;

      const targetLng = Math.max(-180, Math.min(180, lng - offset));
      if (Number.isFinite(targetLng)) {
        // Prevent any queued fitBounds action from overriding the first overview zoom.
        clearPanToBounds();
        map?.panTo({
          lat,
          lng: targetLng,
        });

        // Animate zoom after pan to provide a smooth "zoom into" experience.
        // animateZoomTo(map, targetZoom);

        const INTERMEDIATE_ZOOM = Math.max(10, targetZoom - 3);

        map.setZoom(INTERMEDIATE_ZOOM); // jump slightly out first
        animateZoomTo(map, targetZoom);

        // Guard: listen for zoom_changed and immediately restore targetZoom if
        // anything (late fitBounds, panToCoordinates, etc.) undercuts it during
        // the animation window. The guard is active from now until ~1 second
        // after the last animation step completes.
        /*
      animateZoomTo(map, targetZoom);
      */
        // removed bc it's forcing map to jump to final zoom level during each step of animation (cancels gradual effect)

        const stepsNeeded = Math.abs(targetZoom - currentZoom);
        const guardWindowMs = stepsNeeded * 90 + 1200;
        const guardStart = Date.now();

        const zoomGuard = map.addListener("zoom_changed", () => {
          if (Date.now() - guardStart > guardWindowMs) {
            zoomGuard.remove();
            return;
          }
          const current = map.getZoom() ?? 0;
          if (current < targetZoom) {
            console.warn(
              `[SelectedLocation] zoom_changed dropped to ${current} during animation window — restoring to ${targetZoom}`,
            );
            map.setZoom(targetZoom);
          }
        });

        // Always clean up the listener after the window expires.
        window.setTimeout(() => {
          zoomGuard.remove();
        }, guardWindowMs + 100);
      }
    }
  }, [map, tempPinLocation, isMobile, selectedLocation, clearPanToBounds]);

  useEffect(() => {
    if (!map) return;
    if (!showAddLocationPanel && insightsEditing === undefined) return;
    if (!selectingPinLocation) return;
    if (pinLocation) {
      if (pinLocation.lat == null || pinLocation.lng == null) {
        // Don't panTo when location not provided (null/undefined). Allow 0 coordinates.
        return;
      }

      console.info("Pan to pinLocation", { pinLocation });

      const zoom = map.getZoom() || 0;
      if (zoom < 10) {
        map?.setZoom(10);
      }
      const bounds = map.getBounds();
      const northEast = bounds?.getNorthEast();
      const southWest = bounds?.getSouthWest();
      let offset = 0;

      if (northEast && southWest) {
        const mapWidth = window.innerWidth - 400;
        const percent = mapWidth > 0 ? 0 / mapWidth : 0;

        offset = (northEast.lat() - southWest.lat()) * percent;
        if (!Number.isFinite(offset)) offset = 0;
      }

      const targetLng = Number(pinLocation.lng) - offset;
      if (Number.isFinite(targetLng)) {
        map?.panTo({
          lat: pinLocation.lat,
          lng: targetLng,
        });
      }
    }
  }, [
    map,
    pinLocation,
    selectingPinLocation,
    showAddLocationPanel,
    insightsEditing,
    isMobile,
  ]);

  if (
    pinLocation &&
    (showAddLocationPanel ||
      (showInsightsPanel && insightsEditing === "Facility"))
  ) {
    return <AdvancedMarker position={pinLocation} />;
  }

  return <div />;
};

export default SelectedLocation;
